import React, { useState } from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { socialProfiles, SocialProfile } from '../data/contact';

// Fine-line Instagram mark, drawn to match the site's 1.5px icon stroke
function InstagramIcon({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true" data-id="element-1126" data-mp="1pukqixli0zsr">
      <rect x="3" y="3" width="18" height="18" rx="5" data-id="element-1127" data-mp="1bx5wtgo7q5uz" />
      <circle cx="12" cy="12" r="4" data-id="element-1128" data-mp="1fxptkj4vj7gl" />
      <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" stroke="none" data-id="element-1129" data-mp="1fxzie3e6n8kp" />
    </svg>;
}

// "From a Distance" — the house's Instagram channels: picture, name, handle, followers. Nothing more.
export function SocialChannels() {
  return <div data-id="element-1130" data-mp="2waeojqyjfjb5">
      <h3 className="flex items-center gap-2 text-[14px] font-bold uppercase tracking-[0.02em]" data-id="element-1131" data-mp="2wan27l2hisne">
        From a Distance
        <InstagramIcon className="h-4 w-4 text-ink/60" data-id="element-1132" data-mp="318og7rl201s0" />
      </h3>
      <ul className="mt-6 grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-2" data-id="element-1133" data-mp="2fil3rktqqzhf">
        {socialProfiles.map(p => <li key={p.username} data-id="element-1134" data-mp="2aiyufmh1a3dw">
            <Channel profile={p} data-id="element-1135" data-mp="2ajru63hkrsmj" />
          </li>)}
      </ul>
    </div>;
}
function Channel({
  profile
}: {
  profile: SocialProfile;
}) {
  const external = profile.href.startsWith('http');
  return <a href={profile.href} {...external ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {}} aria-label={external ? `${profile.label} on Instagram${profile.followers ? `, ${profile.followers} followers` : ''}` : profile.label} className="group flex items-center gap-3.5" data-id="element-1136" data-mp="2kbxqtqiruy82">
      <Avatar profile={profile} size="h-11 w-11" data-id="element-1137" data-mp="20lknqekwwsys" />
      <div className="min-w-0 flex-1" data-id="element-1138" data-mp="20nyjyqrbwb7x">
        <p className="flex items-center gap-1.5 text-[15px] leading-[20px]" data-id="element-1139" data-mp="3gzkjregmzygr">
          <span className="truncate transition-opacity duration-200 group-hover:opacity-60" data-id="element-1140" data-mp="3c10xfcy4sknv">{profile.label}</span>
          <ArrowUpRightIcon className="h-3.5 w-3.5 shrink-0 text-ink/40 transition-[transform,color] duration-200 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ink" strokeWidth={1.5} aria-hidden="true" data-id="element-1141" data-mp="29o4epfpdegg6" />
        </p>
        <p className="truncate text-[13px] font-light leading-[18px] text-ink/60" data-id="element-1142" data-mp="2nn46uxc3ddh3">
          {profile.handle}
          {profile.followers && <>
              <span className="mx-1.5 text-ink/30" data-id="element-1143" data-mp="1qv0jhpwi7vjg">·</span>
              <span className="font-normal tabular-nums text-ink" data-id="element-1144" data-mp="1uzqd183owy17">{profile.followers}</span> followers
            </>}
        </p>
      </div>
    </a>;
}

// Profile picture: an uploaded image when provided, otherwise the house's Instagram crest
// (black disc, fine shield outline, script "SL" monogram and "ACCESS" beneath — as on @superluxurymedia)
function Avatar({
  profile,
  size
}: {
  profile: SocialProfile;
  size: string;
}) {
  const [failed, setFailed] = useState(false);
  const showImage = profile.avatar && !failed;
  return <span className={`relative block shrink-0 overflow-hidden rounded-full [clip-path:circle(50%)] ${size}`} data-id="element-1145" data-mp="14mbwlidh9nwn">
      {showImage ? <img src={profile.avatar} alt="" loading="lazy" onError={() => setFailed(true)} className="block h-full w-full rounded-full bg-ink object-cover" data-id="element-1146" data-mp="upz1qh67540b" /> : <SLCrest label={profile.crestLabel ?? 'ACCESS'} data-id="element-1147" data-mp="lqltfrtt6lh6" />}
      {/* Hairline edge so light pictures (e.g. Retreats) still read as a circle on white */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-ink/10" data-id="element-1148" data-mp="bzr4ugt3b9sk" />
    </span>;
}

// Drawn in the same shield as the channel profile pictures: square shoulders, rounded base, SL monogram, word beneath
function SLCrest({
  label
}: {
  label: string;
}) {
  return <svg viewBox="0 0 100 100" className="block h-full w-full" aria-hidden="true" data-id="element-1149" data-mp="3u5dmf2pxthzm">
      <circle cx="50" cy="50" r="50" fill="#0b0b0b" data-id="element-1150" data-mp="1y1z3fv24wmhs" />
      {/* Shield outline */}
      <path d="M24 13 H76 Q79 13 79 16 V68 Q79 86 50 89 Q21 86 21 68 V16 Q21 13 24 13 Z" fill="none" stroke="#f4f2ee" strokeWidth="1.6" data-id="element-1151" data-mp="2242rrk9wjlkh" />
      {/* Script monogram */}
      <text x="45" y="56" textAnchor="middle" fill="#f4f2ee" fontFamily="'Bodoni Moda', 'Didot', 'Times New Roman', serif" fontStyle="italic" fontSize="30" data-id="element-1152" data-mp="1tbiqsd6514wh">
        S
      </text>
      <text x="56" y="53" textAnchor="middle" fill="#f4f2ee" fontFamily="'Bodoni Moda', 'Didot', 'Times New Roman', serif" fontStyle="italic" fontSize="26" data-id="element-1153" data-mp="1jee0c159en9v">
        L
      </text>
      <text x="50" y="72" textAnchor="middle" fill="#f4f2ee" fontFamily="Inter, sans-serif" fontSize="6.5" letterSpacing="2.4" data-id="element-1154" data-mp="1aer8a5yf6tth">
        {label}
      </text>
    </svg>;
}